import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography, Alert, Stack } from '@mui/material';
import { CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { uploadAPI, assetUrl } from '../services/api';

const MAX_MB = 10;
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface Props {
  value: string | null;
  onChange: (url: string | null) => void;
  label?: string;
  guide?: string;
  // 기본 미리보기 대신 앱 화면과 같은 모양으로 보여줄 때
  renderPreview?: (src: string) => React.ReactNode;
}

// 이미지 1장 업로드 필드 (공지/팝업 공용). 업로드 즉시 서버 경로를 onChange로 전달
const ImageUploadField: React.FC<Props> = ({ value, onChange, label = '이미지', guide, renderPreview }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 다시 선택해도 onChange 발생하도록
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      setError('JPG, PNG, WEBP, GIF 이미지만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`이미지는 ${MAX_MB}MB 이하만 업로드할 수 있습니다.`);
      return;
    }

    setError('');
    setUploading(true);
    try {
      const res = await uploadAPI.image(file);
      if (res.data?.url) onChange(res.data.url);
    } catch (err: any) {
      // 413은 서버(nginx) 업로드 용량 제한 - 본문이 JSON이 아님
      const status = err?.response?.status;
      setError(err?.response?.data?.error || (status === 413 ? '서버 업로드 용량 제한을 넘었습니다.' : '이미지 업로드에 실패했습니다.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      {value && (
        <Box sx={{ mb: 1 }}>
          {renderPreview ? renderPreview(assetUrl(value)) : (
            <Box
              component="img"
              src={assetUrl(value)}
              alt=""
              sx={{ display: 'block', maxWidth: '100%', maxHeight: 240, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            />
          )}
        </Box>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {value ? '이미지 변경' : '이미지 업로드'}
        </Button>
        {value && (
          <Button color="error" startIcon={<DeleteIcon />} disabled={uploading} onClick={() => onChange(null)}>
            삭제
          </Button>
        )}
      </Stack>
      <input ref={inputRef} type="file" accept={ACCEPT.join(',')} hidden onChange={handleFile} />
      {guide && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{guide}</Typography>
      )}
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
};

export default ImageUploadField;
